// Mock data for DCA Portfolio 360 - Educational Tool
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// D) AI Trading Performance Fee Adjustment
// Apply 30% weekly performance fee on profits for AI Trading basket
export const applyAITradingFee = (grossMonthlyRate) => {
  // Convert monthly gross rate to weekly gross rate
  const weeklyGrossRate = Math.pow(1 + (grossMonthlyRate / 100), 1/4) - 1;
  
  // Apply 30% performance fee on profits: net_weekly_rate = gross_weekly_rate × (1 – 0.30)
  const netWeeklyRate = weeklyGrossRate * (1 - 0.30);
  
  // Recompound to monthly net rate: (1 + net_weekly_rate)^4
  const netMonthlyRate = (Math.pow(1 + netWeeklyRate, 4) - 1) * 100;
  
  return netMonthlyRate;
};

export const defaultRates = {
  // Panier 1: DCA Bitcoin & Crypto (unified basket - 40% annual)
  dcaCrypto: { rate: 40, period: 'annual', name: 'DCA Bitcoin & Crypto' },
  // Keep old keys for backward compatibility
  bitcoin: { rate: 40, period: 'annual', name: 'DCA Bitcoin & Crypto' },
  diversified: { rate: 40, period: 'annual', name: 'DCA Bitcoin & Crypto' },
  // Panier 2: Trading avec IA - Updated default rates (net rates after fees)
  jairvis: { 
    rate: 3.90, 
    period: 'monthly', 
    name: 'Jairvis',
    hasPerformanceFee: false
  },
  jarvis: { 
    rate: 3.90, 
    period: 'monthly', 
    name: 'Jairvis',
    hasPerformanceFee: false
  },
  sugair: { 
    rate: 4.50, 
    period: 'monthly', 
    name: 'Sugair',
    hasPerformanceFee: false
  },
  sigmai: { 
    rate: 3.48, 
    period: 'monthly', 
    name: 'Sigmai',
    hasPerformanceFee: false
  },
  // Panier 3: Web3
  web3: { rate: 3.50, period: 'monthly', name: 'Web3' }
};

export const profiles = {
  conservative: {
    name: 'Conservative',
    description: 'Lower risk, steady growth focus',
    allocations: {
      dcaCrypto: 50,
      aiTrading: 40,
      web3: 10
    },
    aiSplit: {
      sigmai: 40,
      sugair: 35,
      jairvis: 25
    }
  },
  balanced: {
    name: 'Balanced',
    description: 'Moderate risk, balanced approach',
    allocations: {
      dcaCrypto: 40,
      aiTrading: 40,
      web3: 20
    },
    aiSplit: {
      sigmai: 40,
      sugair: 35,
      jairvis: 25
    }
  },
  exotic: {
    name: 'Exotic',
    description: 'Higher risk, maximum growth potential',
    allocations: {
      dcaCrypto: 30,
      aiTrading: 40,
      web3: 30
    },
    aiSplit: {
      sigmai: 40,
      sugair: 35,
      jairvis: 25
    }
  }
};

export const basketTooltips = {
  dcaCrypto: {
    title: 'DCA Bitcoin & Crypto',
    description: 'Dollar-cost averaging into Bitcoin and top altcoins. Regular purchases to smooth volatility over time.',
    icon: '₿'
  },
  // Keep old keys for backward compatibility
  bitcoin: {
    title: 'DCA Bitcoin & Crypto',
    description: 'Dollar-cost averaging into Bitcoin and top altcoins. Regular purchases to smooth volatility over time.',
    icon: '₿'
  },
  diversified: {
    title: 'DCA Bitcoin & Crypto',
    description: 'Dollar-cost averaging into Bitcoin and top altcoins. Regular purchases to smooth volatility over time.',
    icon: '₿'
  },
  aiTrading: {
    title: 'Trading avec IA',
    description: 'Automated trading bots: Jairvis, Sugair, and Sigmai. Higher returns through algorithmic strategies.',
    icon: '🤖'
  },
  web3: {
    title: 'Web3 Staking & Farming',
    description: 'Earn yield through staking and liquidity farming. Passive income from DeFi protocols.',
    icon: '🌾'
  }
};

// Calculation functions with monthly contribution support
export const calculateCompoundReturns = (principal, rate, period, timeHorizon) => {
  const rateDecimal = rate / 100;
  
  if (period === 'annual') {
    const years = timeHorizon / 365;
    return principal * Math.pow(1 + rateDecimal, years);
  } else if (period === 'monthly') {
    const months = timeHorizon / 30.44; // Average days per month
    return principal * Math.pow(1 + rateDecimal, months);
  }
  
  return principal;
};

// New function to calculate monthly contribution stream with compounding
export const calculateMonthlyContributionStream = (monthlyAmount, allocationPercent, rate, period, months) => {
  if (monthlyAmount === 0 || allocationPercent === 0) return 0;
  
  const monthlyContribution = monthlyAmount * (allocationPercent / 100);
  const rateDecimal = rate / 100;
  let totalValue = 0;
  
  if (period === 'annual') {
    // For annual rates, convert to monthly equivalent
    const monthlyRate = Math.pow(1 + rateDecimal, 1/12) - 1;
    
    for (let month = 1; month <= months; month++) {
      const remainingMonths = months - month;
      const contributionValue = monthlyContribution * Math.pow(1 + monthlyRate, remainingMonths);
      totalValue += contributionValue;
    }
  } else if (period === 'monthly') {
    // Direct monthly compounding
    for (let month = 1; month <= months; month++) {
      const remainingMonths = months - month;
      const contributionValue = monthlyContribution * Math.pow(1 + rateDecimal, remainingMonths);
      totalValue += contributionValue;
    }
  }
  
  return totalValue;
};

export const calculatePortfolioProjections = (totalCapital, allocations, aiSplit, rates, monthlyContribution = 0) => {
  const timeHorizons = [365, 1095, 1825]; // 1 year, 3 years, 5 years
  
  const results = {};
  
  timeHorizons.forEach(days => {
    const months = Math.round(days / 30.44); // Convert days to months
    const projections = {};
    let totalFV = 0;
    
    // Panier 1: DCA Bitcoin & Crypto - Initial capital + monthly contributions
    // Support both new (dcaCrypto) and old (bitcoin + diversified) allocation keys
    const dcaCryptoAllocation = allocations.dcaCrypto !== undefined 
      ? allocations.dcaCrypto 
      : ((allocations.bitcoin || 0) + (allocations.diversified || 0));
    
    const dcaCryptoPrincipal = totalCapital * (dcaCryptoAllocation / 100);
    const dcaCryptoRate = rates.dcaCrypto || rates.bitcoin || defaultRates.dcaCrypto;
    const dcaCryptoInitialFV = calculateCompoundReturns(dcaCryptoPrincipal, dcaCryptoRate.rate, dcaCryptoRate.period, days);
    const dcaCryptoContribFV = calculateMonthlyContributionStream(monthlyContribution, dcaCryptoAllocation, dcaCryptoRate.rate, dcaCryptoRate.period, months);
    projections.dcaCrypto = dcaCryptoInitialFV + dcaCryptoContribFV;
    projections.dcaCryptoContrib = monthlyContribution * (dcaCryptoAllocation / 100) * months;
    // Backward compatibility
    projections.bitcoin = projections.dcaCrypto;
    projections.diversified = 0;
    projections.bitcoinContrib = projections.dcaCryptoContrib;
    projections.diversifiedContrib = 0;
    totalFV += projections.dcaCrypto;
    
    // Panier 2: AI Trading (split between Sigmai 40%, Sugair 35%, Jairvis 25%) - Initial capital + monthly contributions
    const aiTradingPrincipal = totalCapital * (allocations.aiTrading / 100);
    const aiTradingMonthlyContrib = monthlyContribution * (allocations.aiTrading / 100);
    
    // Use the standard AI split order: Sigmai 40%, Sugair 35%, Jairvis 25%
    const sigmaiAllocation = aiSplit.sigmai || 40;
    const sugairAllocation = aiSplit.sugair || 35;
    const jairvisAllocation = aiSplit.jairvis !== undefined ? aiSplit.jairvis : (aiSplit.jarvis || 25);
    
    const sigmaiPrincipal = aiTradingPrincipal * (sigmaiAllocation / 100);
    const sugairPrincipal = aiTradingPrincipal * (sugairAllocation / 100);
    const jairvisPrincipal = aiTradingPrincipal * (jairvisAllocation / 100);
    
    const jairvisRate = rates.jairvis || rates.jarvis;
    const sigmaiInitialFV = calculateCompoundReturns(sigmaiPrincipal, rates.sigmai.rate, rates.sigmai.period, days);
    const sugairInitialFV = calculateCompoundReturns(sugairPrincipal, rates.sugair.rate, rates.sugair.period, days);
    const jairvisInitialFV = calculateCompoundReturns(jairvisPrincipal, jairvisRate.rate, jairvisRate.period, days);
    
    const sigmaiContribFV = calculateMonthlyContributionStream(aiTradingMonthlyContrib, sigmaiAllocation, rates.sigmai.rate, rates.sigmai.period, months);
    const sugairContribFV = calculateMonthlyContributionStream(aiTradingMonthlyContrib, sugairAllocation, rates.sugair.rate, rates.sugair.period, months);
    const jairvisContribFV = calculateMonthlyContributionStream(aiTradingMonthlyContrib, jairvisAllocation, jairvisRate.rate, jairvisRate.period, months);
    
    const sigmaiFV = sigmaiInitialFV + sigmaiContribFV;
    const sugairFV = sugairInitialFV + sugairContribFV;
    const jairvisFV = jairvisInitialFV + jairvisContribFV;
    
    projections.aiTrading = sigmaiFV + sugairFV + jairvisFV;
    projections.aiSplit = { sigmai: sigmaiFV, sugair: sugairFV, jairvis: jairvisFV };
    projections.aiTradingContrib = aiTradingMonthlyContrib * months;
    totalFV += projections.aiTrading;
    
    // Panier 3: Web3 - Initial capital + monthly contributions
    const web3Principal = totalCapital * (allocations.web3 / 100);
    const web3InitialFV = calculateCompoundReturns(web3Principal, rates.web3.rate, rates.web3.period, days);
    const web3ContribFV = calculateMonthlyContributionStream(monthlyContribution, allocations.web3, rates.web3.rate, rates.web3.period, months);
    projections.web3 = web3InitialFV + web3ContribFV;
    projections.web3Contrib = monthlyContribution * (allocations.web3 / 100) * months;
    totalFV += projections.web3;
    
    // Calculate totals and metrics
    projections.total = totalFV;
    projections.totalContributions = monthlyContribution * months;
    projections.totalInvested = totalCapital + (monthlyContribution * months);
    projections.netGain = totalFV - projections.totalInvested;
    projections.roi = projections.totalInvested > 0 ? (projections.netGain / projections.totalInvested) * 100 : 0;
    projections.gain = totalFV - totalCapital;
    projections.gainPercent = totalCapital > 0 ? ((totalFV - totalCapital) / totalCapital) * 100 : 0;
    
    results[days] = projections;
  });
  
  return results;
};

// Generate time series data for charts
export const generateTimeSeriesData = (totalCapital, allocations, aiSplit, rates, days, monthlyContribution = 0) => {
  const dataPoints = [];
  const stepSize = Math.max(1, Math.floor(days / 50)); // ~50 data points
  
  // Support both new (dcaCrypto) and old (bitcoin + diversified) allocation keys
  const dcaCryptoAllocation = allocations.dcaCrypto !== undefined 
    ? allocations.dcaCrypto 
    : ((allocations.bitcoin || 0) + (allocations.diversified || 0));
  
  for (let day = 0; day <= days; day += stepSize) {
    const projections = calculatePortfolioProjections(totalCapital, allocations, aiSplit, rates, monthlyContribution);
    const dayProjection = interpolateProjection(projections, day, monthlyContribution);
    
    dataPoints.push({
      day,
      date: new Date(Date.now() + day * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      value: dayProjection.total,
      dcaCrypto: dayProjection.dcaCrypto || dayProjection.bitcoin,
      bitcoin: dayProjection.bitcoin, // backward compatibility
      diversified: 0, // deprecated
      aiTrading: dayProjection.aiTrading,
      web3: dayProjection.web3
    });
  }
  
  return dataPoints;
};

// Helper function to interpolate between time horizons
const interpolateProjection = (projections, targetDay, monthlyContribution = 0) => {
  if (targetDay <= 365) {
    // Interpolate between 0 and 1 year (365 days)
    const ratio = targetDay / 365;
    return interpolateValues(getInitialValues(projections, monthlyContribution), projections[365], ratio);
  } else if (targetDay <= 1095) {
    // Interpolate between 1 year and 3 years
    const ratio = (targetDay - 365) / (1095 - 365);
    return interpolateValues(projections[365], projections[1095], ratio);
  } else {
    // Interpolate between 3 years and 5 years
    const ratio = (targetDay - 1095) / (1825 - 1095);
    return interpolateValues(projections[1095], projections[1825], ratio);
  }
};

const getInitialValues = (projections, monthlyContribution = 0) => {
  const initial = { ...projections[365] }; // Use 1 year as baseline instead of 90 days
  Object.keys(initial).forEach(key => {
    if (typeof initial[key] === 'number') {
      // Calculate backwards to initial investment (approximation)
      if (monthlyContribution > 0) {
        // With monthly contributions, the initial value is more complex
        initial[key] = initial[key] / Math.pow(1.15, 1.0); // Rough approximation for 1 year
      } else {
        // Without monthly contributions, use simple backwards calculation
        initial[key] = initial[key] / Math.pow(1.2, 1.0); // Rough approximation for 1 year
      }
    }
  });
  return initial;
};

const interpolateValues = (start, end, ratio) => {
  const result = {};
  Object.keys(start).forEach(key => {
    if (typeof start[key] === 'number' && typeof end[key] === 'number') {
      result[key] = start[key] + (end[key] - start[key]) * ratio;
    } else {
      result[key] = end[key]; // For non-numeric values
    }
  });
  return result;
};

// Local storage helpers
export const saveToLocalStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to save to localStorage:', error);
  }
};

export const loadFromLocalStorage = (key, defaultValue = null) => {
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : defaultValue;
  } catch (error) {
    console.warn('Failed to load from localStorage:', error);
    return defaultValue;
  }
};

// MIGRATION: Handle jarvis -> jairvis rename and Jairvis reactivation (was in maintenance)
export const migrateJarvisToJairvis = () => {
  try {
    // Migrate AI split data
    const aiSplitData = loadFromLocalStorage('dca_ai_split', null);
    if (aiSplitData) {
      let changed = false;
      if (aiSplitData.jarvis !== undefined && aiSplitData.jairvis === undefined) {
        aiSplitData.jairvis = aiSplitData.jarvis;
        delete aiSplitData.jarvis;
        changed = true;
        console.log('Migrated AI split: jarvis -> jairvis');
      }
      // Migration: Reactivate Jairvis from maintenance mode (was sigmai:70, sugair:30, jairvis:0)
      if (aiSplitData.sigmai === 70 && aiSplitData.sugair === 30 && (aiSplitData.jairvis === 0 || aiSplitData.jairvis === undefined)) {
        aiSplitData.sigmai = 40;
        aiSplitData.sugair = 35;
        aiSplitData.jairvis = 25;
        changed = true;
        console.log('Migrated AI split: reactivated Jairvis (40/35/25)');
      }
      if (changed) saveToLocalStorage('dca_ai_split', aiSplitData);
    }
    
    // Migrate saved rates: update jairvis rate from 3.48 to 3.9
    const savedRates = loadFromLocalStorage('dca_rates', null);
    if (savedRates) {
      let changed = false;
      if (savedRates.jairvis && savedRates.jairvis.rate === 3.48) {
        savedRates.jairvis.rate = 3.90;
        changed = true;
      }
      if (savedRates.jarvis && savedRates.jarvis.rate === 3.48) {
        savedRates.jarvis.rate = 3.90;
        changed = true;
      }
      if (changed) {
        saveToLocalStorage('dca_rates', savedRates);
        console.log('Migrated rates: Jairvis 3.48 -> 3.90');
      }
    }
    
    // Migrate profiles that might have custom default
    const customDefault = loadFromLocalStorage('dca_custom_default', null);
    if (customDefault && customDefault.jarvis !== undefined && customDefault.jairvis === undefined) {
      customDefault.jairvis = customDefault.jarvis;
      delete customDefault.jarvis;
      saveToLocalStorage('dca_custom_default', customDefault);
      console.log('Migrated custom default: jarvis -> jairvis');
    }
    
  } catch (error) {
    console.warn('Migration failed:', error);
  }
};

// Mock share link generation
export const generateShareLink = (config) => {
  const configString = btoa(JSON.stringify(config));
  return `${window.location.origin}/shared/${configString}`;
};

// Brand colors for charts
export const chartColors = {
  dcaCrypto: '#f7931a',
  bitcoin: '#f7931a', // backward compatibility
  diversified: '#627eea', // deprecated
  aiTrading: '#00f4ff',
  web3: '#12b7ff',
  sigmai: '#ff3030',
  sugair: '#22dd55',
  jairvis: '#00f4ff',
  jarvis: '#00f4ff'
};

export const timeframeOptions = [
  { value: '365', label: '1 Year', days: 365 },
  { value: '1095', label: '3 Years', days: 1095 },
  { value: '1825', label: '5 Years', days: 1825 }
];

// B) Data Export Functions - Improved and clear structure
export const generateExportData = (totalCapital, monthlyContribution, allocations, aiSplit, rates, projections, language = 'en') => {
  const exportData = [];
  const formatCurrency = (val) => `$${val.toFixed(2)}`;
  
  // Headers with translations
  const labels = {
    en: {
      summary: 'PORTFOLIO SUMMARY',
      timeframe: 'Timeframe',
      initialCapital: 'Initial Capital',
      monthlyContrib: 'Monthly Contribution',
      totalInvested: 'Total Invested',
      finalValue: 'Final Value',
      netGain: 'Net Gain',
      roi: 'Return on Investment (ROI)',
      breakdown: 'BASKET BREAKDOWN',
      basket: 'Investment Basket',
      allocation: 'Allocation %',
      invested: 'Amount Invested',
      annualRate: 'Annual Rate',
      monthlyRate: 'Monthly Rate',
      projectedValue: 'Projected Value',
      growth: 'Growth Amount',
      growthPercent: 'Growth %'
    },
    es: {
      summary: 'RESUMEN DEL PORTAFOLIO',
      timeframe: 'Período',
      initialCapital: 'Capital Inicial',
      monthlyContrib: 'Aporte Mensual',
      totalInvested: 'Total Invertido',
      finalValue: 'Valor Final',
      netGain: 'Ganancia Neta',
      roi: 'Retorno de Inversión (ROI)',
      breakdown: 'DESGLOSE POR CANASTA',
      basket: 'Canasta de Inversión',
      allocation: 'Asignación %',
      invested: 'Monto Invertido',
      annualRate: 'Tasa Anual',
      monthlyRate: 'Tasa Mensual',
      projectedValue: 'Valor Proyectado',
      growth: 'Crecimiento',
      growthPercent: 'Crecimiento %'
    },
    fr: {
      summary: 'RÉSUMÉ DU PORTEFEUILLE',
      timeframe: 'Période',
      initialCapital: 'Capital Initial',
      monthlyContrib: 'Contribution Mensuelle',
      totalInvested: 'Total Investi',
      finalValue: 'Valeur Finale',
      netGain: 'Gain Net',
      roi: 'Retour sur Investissement (ROI)',
      breakdown: 'RÉPARTITION PAR PANIER',
      basket: 'Panier d\'Investissement',
      allocation: 'Allocation %',
      invested: 'Montant Investi',
      annualRate: 'Taux Annuel',
      monthlyRate: 'Taux Mensuel',
      projectedValue: 'Valeur Projetée',
      growth: 'Croissance',
      growthPercent: 'Croissance %'
    }
  };
  
  const t = labels[language] || labels.en;
  
  const timeframes = [
    { period: '1 Year', days: 365 },
    { period: '3 Years', days: 1095 },
    { period: '5 Years', days: 1825 }
  ];

  timeframes.forEach(({ period, days }) => {
    const projection = projections[days];
    if (!projection) return;

    // Summary header
    exportData.push({ [t.summary]: `=== ${period} ===` });
    exportData.push({
      [t.timeframe]: period,
      [t.initialCapital]: formatCurrency(totalCapital),
      [t.monthlyContrib]: formatCurrency(monthlyContribution),
      [t.totalInvested]: formatCurrency(projection.totalInvested),
      [t.finalValue]: formatCurrency(projection.total),
      [t.netGain]: formatCurrency(projection.netGain),
      [t.roi]: `${projection.roi.toFixed(2)}%`
    });
    
    exportData.push({});
    exportData.push({ [t.breakdown]: '' });

    // Basket breakdown
    Object.entries(allocations).forEach(([basket, allocation]) => {
      const basketName = basket === 'bitcoin' ? 'Bitcoin DCA' : 
                        basket === 'diversified' ? 'Diversified Crypto' :
                        basket === 'aiTrading' ? 'AI Trading' : 'Web3 Staking';
      const rate = rates[basket];
      
      if (basket === 'aiTrading') {
        // AI Trading sub-baskets
        Object.entries(aiSplit).forEach(([bot, botAllocation]) => {
          const displayBotName = bot === 'jarvis' ? 'Jairvis' : bot.charAt(0).toUpperCase() + bot.slice(1);
          const rateKey = bot === 'jarvis' ? 'jairvis' : bot;
          const botRate = rates[rateKey] || rates[bot];
          const botCapital = totalCapital * (allocation / 100) * (botAllocation / 100);
          const botProjectionKey = bot === 'jarvis' ? 'jairvis' : bot;
          const botProjection = projection.aiSplit[botProjectionKey] || projection.aiSplit[bot];
          
          exportData.push({
            [t.basket]: `${basketName} - ${displayBotName}`,
            [t.allocation]: `${((allocation * botAllocation) / 100).toFixed(1)}%`,
            [t.invested]: formatCurrency(botCapital),
            [t.monthlyRate]: `${botRate?.rate?.toFixed(2)}%`,
            [t.projectedValue]: formatCurrency(botProjection),
            [t.growth]: formatCurrency(botProjection - botCapital),
            [t.growthPercent]: `${(((botProjection - botCapital) / botCapital) * 100).toFixed(2)}%`
          });
        });
      } else {
        // Regular baskets
        const basketCapital = totalCapital * (allocation / 100);
        const basketProjection = projection[basket];
        const rateDisplay = rate?.period === 'annual' ? 
          { [t.annualRate]: `${rate.rate}%` } : 
          { [t.monthlyRate]: `${rate.rate}%` };
        
        exportData.push({
          [t.basket]: basketName,
          [t.allocation]: `${allocation}%`,
          [t.invested]: formatCurrency(basketCapital),
          ...rateDisplay,
          [t.projectedValue]: formatCurrency(basketProjection),
          [t.growth]: formatCurrency(basketProjection - basketCapital),
          [t.growthPercent]: `${(((basketProjection - basketCapital) / basketCapital) * 100).toFixed(2)}%`
        });
      }
    });

    exportData.push({});
    exportData.push({});
  });

  return exportData;
};

export const exportToCSV = (data, filename = 'dca-portfolio-projections.csv') => {
  // Convert data to CSV format
  if (data.length === 0) return;
  
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        // Handle commas and quotes in data
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value || '';
      }).join(',')
    )
  ].join('\n');

  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const exportToExcel = async (data, filename = 'dca-portfolio-projections.xlsx') => {
  try {
    // Dynamic import for xlsx
    const XLSX = await import('xlsx');
    
    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    
    // Add some styling (basic)
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = 0; R <= range.e.r; ++R) {
      for (let C = 0; C <= range.e.c; ++C) {
        const cell_address = XLSX.utils.encode_cell({c: C, r: R});
        if (!ws[cell_address]) continue;
        
        // Header row styling
        if (R === 0) {
          ws[cell_address].s = {
            font: { bold: true },
            fill: { fgColor: { rgb: "0b1c38" } }
          };
        }
      }
    }
    
    // Set column widths
    ws['!cols'] = [
      { wch: 10 }, // Period
      { wch: 25 }, // Basket
      { wch: 15 }, // Allocation
      { wch: 15 }, // InitialAmount
      { wch: 12 }, // GrossRate
      { wch: 12 }, // NetRate
      { wch: 18 }, // HasPerformanceFee
      { wch: 15 }, // FinalValue
      { wch: 15 }, // Growth
      { wch: 15 }  // GrowthPercent
    ];
    
    XLSX.utils.book_append_sheet(wb, ws, "DCA Portfolio Projections");
    
    // Save file
    XLSX.writeFile(wb, filename);
    
    return true;
  } catch (error) {
    console.error('Export to Excel failed:', error);
    return false;
  }
};


// PDF Export Function
export const exportToPDF = async (totalCapital, monthlyContribution, allocations, aiSplit, rates, projections, language = 'en') => {
  try {
    console.log('📄 Starting PDF generation...', { language, totalCapital });
    
    const doc = new jsPDF();
    console.log('✅ jsPDF document created');
    console.log('✅ autoTable available:', typeof doc.autoTable);
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    
    // Translations
    const translations = {
      en: {
        title: 'Simulator 360° - Portfolio Projection Report',
        subtitle: 'Educational Portfolio Analysis',
        summary: 'Portfolio Summary',
        breakdown: 'Basket Breakdown',
        timeframe: 'Timeframe',
        initialCapital: 'Initial Capital',
        monthlyContrib: 'Monthly Contribution',
        totalInvested: 'Total Invested',
        finalValue: 'Final Value',
        netGain: 'Net Gain',
        roi: 'ROI',
        basket: 'Basket',
        allocation: 'Allocation',
        rate: 'Rate',
        projectedValue: 'Projected Value',
        growth: 'Growth',
        disclaimer: 'EDUCATIONAL DISCLAIMER',
        disclaimerText: 'This document contains educational projections only. It is NOT financial advice. Past performance does not guarantee future results. You are solely responsible for your investment decisions.',
        contact: 'For more information: team@thedcaclub.com',
        generatedDate: 'Generated on'
      },
      es: {
        title: 'Simulador 360° - Reporte de Proyección de Portafolio',
        subtitle: 'Análisis Educativo de Portafolio',
        summary: 'Resumen del Portafolio',
        breakdown: 'Desglose por Canasta',
        timeframe: 'Período',
        initialCapital: 'Capital Inicial',
        monthlyContrib: 'Aporte Mensual',
        totalInvested: 'Total Invertido',
        finalValue: 'Valor Final',
        netGain: 'Ganancia Neta',
        roi: 'ROI',
        basket: 'Canasta',
        allocation: 'Asignación',
        rate: 'Tasa',
        projectedValue: 'Valor Proyectado',
        growth: 'Crecimiento',
        disclaimer: 'AVISO EDUCATIVO',
        disclaimerText: 'Este documento contiene proyecciones educativas únicamente. NO es consejo financiero. Los resultados pasados no garantizan rendimientos futuros. Usted es el único responsable de sus decisiones de inversión.',
        contact: 'Para más información: team@thedcaclub.com',
        generatedDate: 'Generado el'
      },
      fr: {
        title: 'Simulateur 360° - Rapport de Projection de Portefeuille',
        subtitle: 'Analyse Éducative de Portefeuille',
        summary: 'Résumé du Portefeuille',
        breakdown: 'Répartition par Panier',
        timeframe: 'Période',
        initialCapital: 'Capital Initial',
        monthlyContrib: 'Contribution Mensuelle',
        totalInvested: 'Total Investi',
        finalValue: 'Valeur Finale',
        netGain: 'Gain Net',
        roi: 'ROI',
        basket: 'Panier',
        allocation: 'Allocation',
        rate: 'Taux',
        projectedValue: 'Valeur Projetée',
        growth: 'Croissance',
        disclaimer: 'AVERTISSEMENT ÉDUCATIF',
        disclaimerText: 'Ce document contient des projections éducatives uniquement. Ce N\'EST PAS un conseil financier. Les performances passées ne garantissent pas les résultats futurs. Vous êtes seul responsable de vos décisions d\'investissement.',
        contact: 'Pour plus d\'informations : team@thedcaclub.com',
        generatedDate: 'Généré le'
      }
    };
    
    const t = translations[language] || translations.en;
    const formatCurrency = (val) => `$${val.toFixed(2)}`;
    
    let yPos = 20;
    
    // Header with gradient effect (simulated with colors)
    doc.setFillColor(0, 244, 255);
    doc.rect(0, 0, pageWidth, 35, 'F');
    
    doc.setTextColor(4, 8, 22);
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text(t.title, pageWidth / 2, 15, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    doc.text(t.subtitle, pageWidth / 2, 25, { align: 'center' });
    
    yPos = 45;
    
    // Date
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(9);
    doc.text(`${t.generatedDate}: ${new Date().toLocaleDateString()}`, pageWidth - 15, yPos, { align: 'right' });
    yPos += 10;
    
    const timeframes = [
      { label: '1 Year', days: 365 },
      { label: '3 Years', days: 1095 },
      { label: '5 Years', days: 1825 }
    ];
    
    timeframes.forEach(({ label, days }) => {
      const projection = projections[days];
      if (!projection) return;
      
      // Check if we need a new page
      if (yPos > pageHeight - 60) {
        doc.addPage();
        yPos = 20;
      }
      
      // Timeframe header
      doc.setFillColor(18, 183, 255);
      doc.rect(10, yPos, pageWidth - 20, 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(`${t.timeframe}: ${label}`, 15, yPos + 5.5);
      yPos += 12;
      
      // Summary table
      doc.autoTable({
        startY: yPos,
        head: [[t.summary, '']],
        body: [
          [t.initialCapital, formatCurrency(totalCapital)],
          [t.monthlyContrib, formatCurrency(monthlyContribution)],
          [t.totalInvested, formatCurrency(projection.totalInvested)],
          [t.finalValue, formatCurrency(projection.total)],
          [t.netGain, formatCurrency(projection.netGain)],
          [t.roi, `${projection.roi.toFixed(2)}%`]
        ],
        theme: 'grid',
        headStyles: { fillColor: [11, 28, 56], textColor: [0, 244, 255], fontStyle: 'bold' },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: { 0: { fontStyle: 'bold', cellWidth: 60 }, 1: { halign: 'right' } }
      });
      
      yPos = doc.lastAutoTable.finalY + 8;
      
      // Breakdown table - Updated for 3 baskets
      const breakdownData = [];
      
      // Handle dcaCrypto (unified basket)
      const dcaCryptoAllocation = allocations.dcaCrypto !== undefined 
        ? allocations.dcaCrypto 
        : ((allocations.bitcoin || 0) + (allocations.diversified || 0));
      
      if (dcaCryptoAllocation > 0) {
        const dcaRate = rates.dcaCrypto || rates.bitcoin;
        const dcaCapital = totalCapital * (dcaCryptoAllocation / 100);
        const dcaProjection = projection.dcaCrypto || projection.bitcoin || 0;
        
        breakdownData.push([
          'DCA Bitcoin & Crypto',
          `${dcaCryptoAllocation}%`,
          `${dcaRate?.rate || 40}%`,
          formatCurrency(dcaProjection),
          formatCurrency(dcaProjection - dcaCapital)
        ]);
      }
      
      // Handle AI Trading
      const aiAllocation = allocations.aiTrading || 0;
      if (aiAllocation > 0) {
        // Add each AI bot
        const bots = [
          { key: 'sigmai', name: 'Sigmai' },
          { key: 'sugair', name: 'Sugair' },
          { key: 'jairvis', name: 'Jairvis (maintenance)' }
        ];
        
        bots.forEach(({ key, name }) => {
          const botAllocation = aiSplit[key] !== undefined ? aiSplit[key] : (key === 'jairvis' ? (aiSplit.jarvis || 0) : 0);
          if (botAllocation > 0) {
            const botRate = rates[key] || rates.jarvis;
            const botCapital = totalCapital * (aiAllocation / 100) * (botAllocation / 100);
            const botProjection = projection.aiSplit?.[key] || projection.aiSplit?.jarvis || botCapital;
            
            breakdownData.push([
              `AI Trading - ${name}`,
              `${((aiAllocation * botAllocation) / 100).toFixed(1)}%`,
              `${botRate?.rate?.toFixed(2) || '0.00'}%`,
              formatCurrency(botProjection),
              formatCurrency(botProjection - botCapital)
            ]);
          }
        });
      }
      
      // Handle Web3
      const web3Allocation = allocations.web3 || 0;
      if (web3Allocation > 0) {
        const web3Rate = rates.web3;
        const web3Capital = totalCapital * (web3Allocation / 100);
        const web3Projection = projection.web3 || 0;
        
        breakdownData.push([
          'Web3 Staking & Farming',
          `${web3Allocation}%`,
          `${web3Rate?.rate || 3.5}%`,
          formatCurrency(web3Projection),
          formatCurrency(web3Projection - web3Capital)
        ]);
      }
      
      doc.autoTable({
        startY: yPos,
        head: [[t.breakdown, t.allocation, t.rate, t.projectedValue, t.growth]],
        body: breakdownData,
        theme: 'striped',
        headStyles: { fillColor: [11, 28, 56], textColor: [0, 244, 255], fontStyle: 'bold' },
        styles: { fontSize: 8, cellPadding: 2 },
        columnStyles: { 
          0: { cellWidth: 50 },
          1: { halign: 'center', cellWidth: 25 },
          2: { halign: 'center', cellWidth: 25 },
          3: { halign: 'right', cellWidth: 35 },
          4: { halign: 'right', cellWidth: 35 }
        }
      });
      
      yPos = doc.lastAutoTable.finalY + 10;
    });
    
    // Disclaimer on last page
    doc.addPage();
    yPos = 20;
    
    // Disclaimer box
    doc.setFillColor(255, 243, 205);
    doc.rect(10, yPos, pageWidth - 20, 50, 'F');
    doc.setDrawColor(255, 193, 7);
    doc.setLineWidth(1);
    doc.rect(10, yPos, pageWidth - 20, 50);
    
    doc.setTextColor(180, 83, 9);
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text(t.disclaimer, pageWidth / 2, yPos + 10, { align: 'center' });
    
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    const disclaimerLines = doc.splitTextToSize(t.disclaimerText, pageWidth - 30);
    doc.text(disclaimerLines, 15, yPos + 20);
    
    yPos += 60;
    
    // Contact info
    doc.setFillColor(11, 28, 56);
    doc.rect(10, yPos, pageWidth - 20, 15, 'F');
    doc.setTextColor(0, 244, 255);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text(t.contact, pageWidth / 2, yPos + 10, { align: 'center' });
    
    // Save PDF
    const filename = `simulator-360-report-${new Date().toISOString().split('T')[0]}.pdf`;
    console.log('💾 Saving PDF:', filename);
    doc.save(filename);
    console.log('✅ PDF saved successfully');
    return true;
  } catch (error) {
    console.error('Export to PDF failed:', error);
    return false;
  }
};